import type { MultiAssetValueOverview, SingleAssetValueOverview } from '@einwert/valuation-api-client'
import { useMutation, useQuery } from '@tanstack/react-query'
import { getValuationApiClient } from 'ClientPortal/src/api/valuationApiClient'
import { ReactQueryOptions, WithReactQueryOptions } from 'ComponentLibrary'
import type { MultiPortfolioEnergySummary, MultiPortfolioValueOverview } from '@einwert/valuation-api-client'

import { compact } from 'lodash'
import { getSelectedCustomer, useSelectedCustomer } from 'ClientPortal/src/context/SelectedCustomerContext'
import invariant from 'tiny-invariant'
import { ALL_PORTFOLIOS } from 'ClientPortal/src/pages/Dashboard/useSelectedPortfolio'
import { useGlobalMutationErrorHandler } from 'ClientPortal/src/api/useGlobalMutationErrorHandler'

export const VALUATION = 'VALUATION'
export const SINGLE_ASSET_VALUE_OVERVIEW = 'SINGLE_ASSET_VALUE_OVERVIEW'
export const DEVIATION_ANALYSIS = 'DEVIATION_ANALYSIS'
export const MULTI_ASSET_VALUE_OVERVIEW = 'MULTI_ASSET_VALUE_OVERVIEW'
export const MULTI_PORTFOLIO_VALUE_OVERVIEW = 'MULTI_PORTFOLIO_VALUE_OVERVIEW'
export const ASSET_ENERGY_SUMMARY = 'ASSET_ENERGY_SUMMARY'
export const MULTI_PORTFOLIO_ENERGY_SUMMARY = 'MULTI_PORTFOLIO_ENERGY_SUMMARY'

type DeviationAnalysisBaseParams =
    | {
          baseValuationId?: string
          baseValueCheckId?: never
      }
    | {
          baseValueCheckId?: string
          baseValuationId?: never
      }

type DeviationAnalysisComparisonParams =
    | {
          comparisonValuationId?: string
          comparisonValueCheckId?: never
      }
    | {
          comparisonValueCheckId?: string
          comparisonValuationId?: never
      }

type DeviationAnalysisQueryParams = DeviationAnalysisBaseParams & DeviationAnalysisComparisonParams

export function useGetSingleAssetValueOverview({
    throwOnError,
    assetId,
    valuationId,
    retry,
}: WithReactQueryOptions<{ assetId?: string; valuationId?: string }, SingleAssetValueOverview>) {
    const valuationApiClient = getValuationApiClient()
    const getSingleAssetValueOverviewQuery = () =>
        valuationApiClient.assets.getSingleAssetValueOverview({ assetId, valuationId, customerId: getSelectedCustomer()?.uuid })
    const { data, isPending } = useQuery({
        queryFn: getSingleAssetValueOverviewQuery,
        queryKey: valuationId ? [SINGLE_ASSET_VALUE_OVERVIEW, assetId, VALUATION, valuationId] : [SINGLE_ASSET_VALUE_OVERVIEW, assetId],
        enabled: !!assetId,
        throwOnError,
        retry,
    })
    return { singleAssetValueOverview: data, isSingleAssetValueOverviewPending: isPending }
}

export function useGetFilteredMultiAssetValueOverview({
    throwOnError,
    assetIds,
    retry,
}: WithReactQueryOptions<{ assetIds?: string[] }, MultiAssetValueOverview> = {}) {
    const valuationApiClient = getValuationApiClient()
    const getMultiAssetValueOverviewQuery = () =>
        valuationApiClient.assets.getMultiAssetValueOverview({ assetIds, customerId: getSelectedCustomer()?.uuid })
    const { data, isPending } = useQuery({
        queryFn: getMultiAssetValueOverviewQuery,
        queryKey: compact([MULTI_ASSET_VALUE_OVERVIEW, assetIds?.sort().join(',')]),
        throwOnError,
        retry,
    })
    return {
        multiAssetValueOverviewData: data,
        isMultiAssetValueOverviewDataPending: isPending,
    }
}

export function useGetMultiAssetValueOverview({ throwOnError, retry }: ReactQueryOptions<MultiAssetValueOverview> = {}) {
    const valuationApiClient = getValuationApiClient()
    const getMultiAssetValueOverviewQuery = () =>
        valuationApiClient.assets.getMultiAssetValueOverview({ customerId: getSelectedCustomer()?.uuid })
    const { data, isPending, isError } = useQuery({
        queryFn: getMultiAssetValueOverviewQuery,
        queryKey: [MULTI_ASSET_VALUE_OVERVIEW],
        throwOnError,
        retry,
    })
    return {
        multiAssetValueOverviewData: data,
        isMultiAssetValueOverviewDataPending: isPending,
        isError,
    }
}

export function useGetAllPortfoliosValueOverviewExportFile({
    selectedPortfolioId,
    onSuccess,
}: {
    selectedPortfolioId?: string
    onSuccess: (url: string) => void
}) {
    const { selectedCustomer } = useSelectedCustomer()
    const valuationApiClient = getValuationApiClient()
    const handleError = useGlobalMutationErrorHandler()

    const { mutate, isPending } = useMutation({
        mutationFn: () =>
            valuationApiClient.portfolios.getAllPortfolioValueOverviewExportUrl({
                customerId: selectedCustomer?.uuid,
                portfolioId: selectedPortfolioId === ALL_PORTFOLIOS ? undefined : selectedPortfolioId,
            }),
        onSuccess,
        onError: handleError,
    })
    return { generateValueOverviewExportFileUrl: mutate, isGenerateValueOverviewExportFileUrl: isPending }
}

export function useGetAllPortfoliosValueOverview({ throwOnError, retry }: ReactQueryOptions<MultiPortfolioValueOverview> = {}) {
    const valuationApiClient = getValuationApiClient()
    const { selectedCustomer } = useSelectedCustomer()
    const getValueOverviewQuery = () => valuationApiClient.portfolios.getAllPortfoliosValueOverview({ customerId: selectedCustomer?.uuid })
    const { data, isPending } = useQuery({
        queryFn: getValueOverviewQuery,
        queryKey: [MULTI_PORTFOLIO_VALUE_OVERVIEW],
        throwOnError,
        retry,
    })
    return { multiPortfolioValueOverview: data, isMultiPortfolioValueOverviewPending: isPending }
}

export function useGetDeviationAnalysis({
    baseValuationId,
    comparisonValuationId,
    baseValueCheckId,
    comparisonValueCheckId,
    ...options
}: Omit<WithReactQueryOptions<DeviationAnalysisQueryParams>, 'enabled'>) {
    const valuationApiClient = getValuationApiClient()
    const { selectedCustomer } = useSelectedCustomer()
    const baseReferenceId = baseValueCheckId ?? baseValuationId
    const comparisonReferenceId = comparisonValueCheckId ?? comparisonValuationId
    const getDeviationAnalysisQuery = () => {
        invariant(!(baseValuationId && baseValueCheckId), 'base valuation ID and base value check ID are mutually exclusive')
        invariant(
            !(comparisonValuationId && comparisonValueCheckId),
            'comparison valuation ID and comparison value check ID are mutually exclusive'
        )

        invariant(baseValueCheckId || baseValuationId, 'base valuation ID must be defined')
        invariant(comparisonValueCheckId || comparisonValuationId, 'comparison valuation ID must be defined')
        return valuationApiClient.valuations.getDeviationAnalysis({
            customerId: selectedCustomer?.uuid,
            baseValueCheckId,
            baseValuationId,
            comparisonValueCheckId,
            comparisonValuationId,
        })
    }

    const { data, isPending, isError } = useQuery({
        queryFn: getDeviationAnalysisQuery,
        queryKey: [DEVIATION_ANALYSIS, baseReferenceId, comparisonReferenceId],
        ...options,
        enabled: !!baseReferenceId && !!comparisonReferenceId,
    })
    return { data, isPending, isError }
}

export function useGetAllPortfoliosEnergySummary({
    throwOnError,
    retry,
    enabled,
}: Pick<ReactQueryOptions<MultiPortfolioEnergySummary>, 'retry' | 'throwOnError' | 'enabled'> = {}) {
    const valuationApiClient = getValuationApiClient()
    const { selectedCustomer } = useSelectedCustomer()
    const getEnergySummaryQuery = () => valuationApiClient.portfolios.getAllPortfoliosEnergySummary({ customerId: selectedCustomer?.uuid })
    const { data, isLoading } = useQuery({
        queryFn: getEnergySummaryQuery,
        queryKey: [MULTI_PORTFOLIO_ENERGY_SUMMARY, 'CUSTOMER', selectedCustomer?.uuid],
        throwOnError,
        retry,
        enabled,
    })
    return { multiPortfolioEnergySummary: data, isMultiPortfolioEnergySummaryLoading: isLoading }
}

export function useGetAssetEnergySummary({
    throwOnError,
    assetId,
    retry,
    enabled,
}: WithReactQueryOptions<{ assetId?: string }, SingleAssetValueOverview>) {
    const valuationApiClient = getValuationApiClient()
    const getEnergySummaryQuery = () =>
        valuationApiClient.assets.getAssetEnergySummary({ assetId, customerId: getSelectedCustomer()?.uuid })
    const { data, isLoading } = useQuery({
        queryFn: getEnergySummaryQuery,
        queryKey: [ASSET_ENERGY_SUMMARY, assetId],
        throwOnError,
        retry,
        enabled,
    })
    return { assetEnergySummary: data, isAssetEnergySummaryLoading: isLoading }
}
